/// <amd-module name="DRITools/DRIToolsHub/Callgraph/CallgraphTab" />
import * as React from "react";

// VSS
import { ObservableValue } from "VSS/Core/Observable";
import { ErrorBoundary } from "VSS/Platform/Components/ErrorBoundary";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import {
    Components,
    IVssComponentContext,
    VssComponent
} from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { getCurrentRoute } from "VSS/Platform/Navigation";

// VSSUI
import { Card } from "VSSUI/Card";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";

// DRITools
import { Obj, RequestNode } from "DRITools/Core/Contracts/DRITools";
import { DRIToolsZeroData } from "DRITools/Core/Components/DRIToolsZeroData";
import { CallgraphFilterBar } from "DRITools/DRIToolsHub/Callgraph/CallgraphFilterBar";
import { CallgraphDetailsPanel } from "DRITools/DRIToolsHub/Callgraph/CallgraphDetailsPanel";
import { CallgraphSlowTraceDialog } from "DRITools/DRIToolsHub/Callgraph/CallgraphSlowTraceDialog";
import { CallgraphTable } from "DRITools/DRIToolsHub/Callgraph/CallgraphTable";
import * as Resources from "DRITools/DRIToolsHub/Resources";

interface CallgraphData {
    callgraphResults: RequestNode[];
    endTime: Date;
    maxDepth: number;
    startTime: Date;
}

export class CallgraphTab extends VssComponent {
    private callgraphData: ObservableValue<CallgraphData | undefined> = new ObservableValue(undefined);
    private callgraphDataProvider = "ms.vss-dri-tools-web.callgraph-data-provider";
    private callgraphHubRef: React.RefObject<HTMLDivElement>;
    private contributionService: IVssContributionService;
    private contentWidth = new ObservableValue<number>(1700);
    private currentNode: ObservableValue<RequestNode | undefined> = new ObservableValue(undefined);
    private currentSlowTraces: ObservableValue<Obj[] | undefined> = new ObservableValue(undefined);
    private driToolsRoute = "ms.vss-dri-tools-web.dri-tools-route";
    private errorMessage: ObservableValue<string | undefined> = new ObservableValue(undefined);

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.callgraphHubRef = React.createRef();
        this.contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
    }

    public componentDidMount() {
        window.addEventListener("resize", () => {
            if (this.callgraphHubRef.current) {
                this.contentWidth.value = this.callgraphHubRef.current.offsetWidth;
            }
        });

        if (this.callgraphHubRef.current) {
            this.contentWidth.value = this.callgraphHubRef.current.offsetWidth;
        }

        this.callgraphData.value = this.contributionService.getData<CallgraphData>(this.callgraphDataProvider);
        const dataProviderExceptions = this.contributionService.getDataProviderExceptions();

        if (dataProviderExceptions && dataProviderExceptions[this.callgraphDataProvider]) {
            this.callgraphData.value = undefined;
            this.errorMessage.value = dataProviderExceptions[this.callgraphDataProvider].message;
        }
    }

    public componentWillUnmount(): void {
        window.addEventListener("resize", () => { });
    }

    public render(): JSX.Element {
        return (
            <ErrorBoundary handlerComponentType="tfs.page-error-handler">
                <div className="flex-row flex-grow flex-noshrink justify-center" ref={this.callgraphHubRef}>
                    <div className="flex-center rhythm-vertical-16 flex-grow">
                        <CallgraphFilterBar onApply={this.onApplyFilter} onError={() => this.errorMessage.value = Resources.Callgraph_GUIDInvalid} />
                        <Observer errorMessage={this.errorMessage}>
                            {(observedProps: { errorMessage: string | undefined }) => {
                                return observedProps.errorMessage ? (
                                    <MessageCard
                                        className="flex-self-stretch margin-bottom-16"
                                        severity={MessageCardSeverity.Error}
                                        onDismiss={() => {
                                            this.errorMessage.value = undefined;
                                        }}
                                    >
                                        {observedProps.errorMessage}
                                    </MessageCard>
                                ) : null;
                            }}
                        </Observer>
                        <Card contentProps={{ contentPadding: false }} titleProps={{ text: "" }}>
                            <Observer callgraphData={this.callgraphData} contentWidth={this.contentWidth}>
                                {(observedProps: { callgraphData: CallgraphData | undefined, contentWidth: number }) => {
                                    if (observedProps.callgraphData && observedProps.callgraphData.callgraphResults && observedProps.callgraphData.callgraphResults.length > 0) {
                                        return <CallgraphTable
                                            endTime={observedProps.callgraphData.endTime}
                                            maxDepth={observedProps.callgraphData.maxDepth}
                                            requests={observedProps.callgraphData.callgraphResults}
                                            startTime={observedProps.callgraphData.startTime}
                                            width={observedProps.contentWidth}
                                            onSelectItem={(node: RequestNode) => {
                                                this.currentNode.value = node;
                                            }}
                                            onShowSlowTraces={(traces: Obj[]) => {
                                                this.currentSlowTraces.value = traces;
                                            }}
                                        />;
                                    }
                                    return <DRIToolsZeroData isInvalid={!observedProps.callgraphData} />;
                                }}
                            </Observer>
                        </Card>
                        <Observer currentNode={this.currentNode}>
                            {(observedProps: { currentNode: RequestNode | undefined }) => {
                                if (observedProps.currentNode) {
                                    return <CallgraphDetailsPanel node={observedProps.currentNode.data} onDismiss={() => {
                                        this.currentNode.value = undefined
                                    }} />
                                }
                                return null;
                            }}
                        </Observer>
                        <Observer currentSlowTraces={this.currentSlowTraces}>
                            {(observedProps: { currentSlowTraces: Obj[] | undefined }) => {
                                if (observedProps.currentSlowTraces && observedProps.currentSlowTraces.length > 0) {
                                    return <CallgraphSlowTraceDialog slowTraces={observedProps.currentSlowTraces} onDismiss={() => {
                                        this.currentSlowTraces.value = undefined
                                    }} />
                                }
                                return null;
                            }}
                        </Observer>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }

    private onApplyFilter = (id: string) => {
        const route = getCurrentRoute(this.context.pageContext);
        const routeValues: { [key: string]: string } = {
            project: route.routeValues.project
        }

        let href = routeUrl(this.context.pageContext, this.driToolsRoute, routeValues);
        href += `?id=${id}`;

        onClickFPS(this.context.pageContext, href, true);
    };
}

Components.add("CallgraphTab", CallgraphTab);
